/**
 * 選んだ言語に訳が入っていないことを示すバッジ。
 *
 * 未翻訳を原文（日本語）で埋めない、という決定の表示側である。空欄の代わりに原文を出すと、
 * 訳の済んだ本文と区別が付かず、翻訳の残作業が画面から永久に見えなくなる。本文が空のまま
 * この印だけを出すことで、「書かれていない」ではなく「訳されていない」と名指しする。
 *
 * `warning` にするのは、制約違反や宙吊り参照と同じく**残 TODO** だからである。障害ではない
 * ので `error` にはしない。未設定（`spec.unset`）とは別の語にしてある。原文が無い状態と
 * 訳が無い状態は直す相手が違い（前者は仕様を書く人、後者は訳す人）、同じ見た目にすると
 * どちらの残作業か読み取れない。
 *
 * `untranslated` が `false` のときは何も描かない。翻訳済みであることを示す印を全行に出すと、
 * 揃っているのが既定であるはずの状態が例外のように見え、残りの未翻訳が埋もれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選んだ言語に訳が入っていない本文へ未翻訳の印を付ける。
 * @graph-connects none
 */
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useTranslation } from 'react-i18next';

/** `UntranslatedBadge` の props。 */
export interface UntranslatedBadgeProps {
  /** 選んだ言語に本文が入っていないか。読取応答の `untranslated` をそのまま渡す。 */
  untranslated: boolean;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 未翻訳の本文にだけ残 TODO の印を描く。
 * @graph-connects none
 */
export function UntranslatedBadge({ untranslated }: UntranslatedBadgeProps) {
  const { t } = useTranslation();

  if (!untranslated) return null;

  return (
    <Badge variant="warning" size="sm" data-untranslated="true">
      {t('spec.untranslated')}
    </Badge>
  );
}

/**
 * 表示名の穴埋めに使う翻訳関数。呼び出し側は `useTranslation` の `t` をそのまま渡す。
 *
 * **識別子（要素の番号）を受ける引数を持たせない**ための型である。以前の `displayLabel` は
 * 2 つ目の引数に穴埋めの文字列を取り、呼び出し側のほぼ全て（40 箇所以上）が要素の番号を
 * 渡していた。その結果、名前の材料が未設定の要素は画面に「773」「1369」のような裸の番号で
 * 出た。人は番号で要素を判別できない。文字列を受ける口を残す限り番号は流れ込むので、口の型を
 * 関数にして番号を渡せなくした。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 表示名の空欄を埋める言葉を引く翻訳関数の型
 * @graph-connects none
 */
export type LabelTranslate = (key: string) => string;

/**
 * 表示名として実際に描く文字列を決める。**空欄の穴埋めをする唯一の所有者**である。
 *
 * 空欄は**人が読む言葉**で埋める。訳が無いだけのノードは「未翻訳」、名前の材料が無いノードは
 * 「未設定」（読取側の `UNSET_ELEMENT_LABEL` と同じ語）。**要素の番号では埋めない**。番号は
 * 遷移のための鍵で、画面に並べる値ではない（番号を残したいときは `data-id` / `title` に置く）。
 *
 * 原文（日本語）で埋めないのは、訳の済んだ本文と混ざって翻訳の残作業が画面から見えなく
 * なるためで、`UntranslatedBadge` と同じ理由による。
 *
 * 読取が返す本文は選んだ言語へ畳んだ後なので、**原文はあるが訳が無い**ノードも空文字になる。
 * 未翻訳の旗を見ずに空だけで判定すると、訳す人の残作業（未翻訳）が仕様を書く人の残作業
 * （未設定）として出る。直す相手が違うので、旗で語を分ける。
 *
 * @param label 選んだ言語へ畳んだ表示名。空なら穴埋めする
 * @param translate `useTranslation` の `t`
 * @param untranslated 選んだ言語に訳が入っていないか。読取応答の `untranslated`（無い応答は省く）
 * @returns 表示名。空なら「未設定」または「未翻訳」
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 表示名が空のとき「未設定」「未翻訳」の言葉で埋めて、行から名前も番号も出さずに済ませる
 * @graph-connects none
 */
export function displayLabel(
  label: string | null,
  translate: LabelTranslate,
  untranslated = false,
): string {
  const value = label ?? '';
  if (value !== '') return value;
  return translate(untranslated ? 'spec.untranslated' : 'spec.unset');
}

/** `SpecLabelBadges` の props。 */
export interface SpecLabelBadgesProps {
  /** 選んだ言語へ畳んだ表示名。`null` は本文そのものが無いことを表す。 */
  label: string | null;
  /** 選んだ言語に訳が入っていないか。読取応答の `untranslated` をそのまま渡す。 */
  untranslated: boolean;
}

/**
 * 表示名に添える印を描く。**名前のある行にだけ**未翻訳の印を添える。
 *
 * 表示名が空の行は、本文（`displayLabel`）がすでに「未設定」「未翻訳」と言っている。ここで
 * 同じ語の印を重ねると「未設定 未記入」「未翻訳 未翻訳」のように同じ事実が 2 度出る。印が
 * 要るのは、名前は読めるが選んだ言語の訳が欠けている行だけである。
 *
 * 呼び出し側は表示文字列を `displayLabel` で作り、その隣にこれを置くだけでよい。表示文字列と
 * 印を別にしてあるのは、文字列だけをリンクの内側に入れたい行があるためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 名前のある表示名の隣にだけ未翻訳の印を描き、空欄の行では本文の言葉に任せる
 * @graph-connects UntranslatedBadge [calls] 未翻訳の印
 */
export function SpecLabelBadges({ label, untranslated }: SpecLabelBadgesProps) {
  if ((label ?? '') === '') return null;
  return <UntranslatedBadge untranslated={untranslated} />;
}
